import type { ConversationMessage } from '@kimuno/api-sdk';
import { borderRadius, palette, spaces } from '@kimuno/theme';
import { Box, Stack, Typography } from '@mui/material';
import type React from 'react';
import { useContext } from 'react';

import type { ConversationNewMessage } from '../../../../../reducers/aiAssistant.reducers';
import { aiAssistantContext } from '../../../../../state/AIAssistantContextProvider';
import { Linkify } from '../../../../../utils/Linkify';
import AIUploadFileList from '../../Footer/PromptInput/AIUploadFileList';

const AIAssistantUserMessage: React.FC<{
	message: ConversationNewMessage | ConversationMessage;
}> = ({ message }) => {
	const { lastUserMessageRef } = useContext(aiAssistantContext);
	const messageText: string = message.content[0].text as string;
	const links = message.context?.links;

	// Concatenate messageText with links if they exist
	const fullText = links && links.length > 0 ? `${messageText}\n\n${links.join('\n')}` : messageText;

	const shouldShowMessage = fullText.trim();

	return (
		<Stack
			ref={lastUserMessageRef}
			data-testid="ai-assistant-user-message"
			alignItems="flex-end"
			gap={spaces.lg}
			sx={{
				marginBottom: spaces.lg,
				alignSelf: 'flex-end',
				width: '100%',
			}}
		>
			{message.context?.attachments && (
				<AIUploadFileList uploadedAttachments={message.context.attachments} />
			)}
			{shouldShowMessage && (
				<Box
					sx={{
						borderRadius: borderRadius.md,
						maxWidth: `calc(100% - ${spaces.xxxxl})`,
						paddingY: spaces.sm,
						paddingX: spaces.md,
						backgroundColor: palette.primary.contrast,
					}}
				>
					<Typography
						variant="body2"
						sx={{
							color: palette.text.primary,
							whiteSpace: 'pre-wrap',
							overflowWrap: 'anywhere',
							wordBreak: 'break-word',
						}}
					>
						<Linkify
							linkStyle={{
								color: palette.primary.main,
								textDecoration: 'underline',
							}}
						>
							{fullText}
						</Linkify>
					</Typography>
				</Box>
			)}
		</Stack>
	);
};

export default AIAssistantUserMessage;
